/**
 * iter916/917 — Master Tailor capture. Shown on the Cashier POS only when
 * the branch business_type is 'tailor'. Everything rides checkout as
 * `metadata` on the SAME pos_orders document (no new tables):
 *   { customer_name, customer_phone, ready_date, modification_only,
 *     modification_notes, measurements{...}, photo_url }
 * All labels are bilingual EN / AR (mirrored on the printed receipt via
 * lib/tailorReceipt.js).
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Ruler, Camera, X, User, Phone, CalendarClock } from 'lucide-react';

const FIELDS = [
  { key: 'length', label: 'Length / الطول' },
  { key: 'shoulder', label: 'Shoulder / الكتف' },
  { key: 'chest', label: 'Chest / الصدر' },
  { key: 'sleeve', label: 'Sleeve / الكم' },
  { key: 'neck', label: 'Neck / الرقبة' },
  { key: 'waist', label: 'Waist / الخصر' },
];

const QUICK_DAYS = [
  { days: 1, label: '1 Day / يوم' },
  { days: 3, label: '3 Days / ٣ أيام' },
  { days: 7, label: '1 Week / أسبوع' },
];

const plusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const EMPTY = { customer_name: '', customer_phone: '', ready_date: '', modification_only: false, modification_notes: '', measurements: {}, photo_url: '' };

export const TailorMeasurementsDialog = ({ open, onOpenChange, value, onSave }) => {
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Re-sync from the parent every time the dialog opens so a cleared sale
  // (post-checkout) never leaks the previous customer's data.
  useEffect(() => {
    if (open) setForm({ ...EMPTY, ...(value || {}), measurements: { ...(value?.measurements || {}) } });
  }, [open, value]);

  const setMeas = (key, v) => setForm((f) => ({ ...f, measurements: { ...f.measurements, [key]: v } }));

  // iter918 — Measurement history: when a phone number is entered, load the
  // most recent order for that number and auto-fill (without clobbering
  // anything the cashier already typed).
  const [historyChecked, setHistoryChecked] = useState('');
  useEffect(() => {
    const phone = (form.customer_phone || '').trim();
    if (!open || phone.length < 7 || phone === historyChecked) return undefined;
    const t = setTimeout(async () => {
      try {
        const token = localStorage.getItem('cashier_token');
        const { data } = await api.get('/cashier/tailor/history', {
          params: { phone },
          headers: { Authorization: `Bearer ${token}` },
        });
        setHistoryChecked(phone);
        if (!data.found) return;
        const md = data.metadata || {};
        setForm((f) => {
          const noMeasYet = !Object.values(f.measurements || {}).some((v) => v !== '' && v != null);
          return {
            ...f,
            customer_name: f.customer_name || md.customer_name || '',
            measurements: noMeasYet && !f.modification_only && md.measurements
              ? { ...md.measurements }
              : f.measurements,
          };
        });
        toast.info(`Loaded history from order #${data.order_number} / تم تحميل القياسات السابقة`);
      } catch { /* silent — history is best-effort */ }
    }, 600);
    return () => clearTimeout(t);
  }, [form.customer_phone, open, historyChecked]);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('cashier_token');
      const { data } = await api.post('/cashier/measurements/photo', fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, photo_url: data.image_url }));
      toast.success('Photo attached / تم إرفاق الصورة');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = () => {
    const measurements = {};
    FIELDS.forEach(({ key }) => {
      const v = form.measurements?.[key];
      if (v !== '' && v != null) measurements[key] = v;
    });
    onSave({
      customer_name: form.customer_name?.trim() || null,
      customer_phone: form.customer_phone?.trim() || null,
      ready_date: form.ready_date || null,
      modification_only: !!form.modification_only,
      modification_notes: form.modification_only ? (form.modification_notes?.trim() || null) : null,
      measurements: form.modification_only ? {} : measurements,
      photo_url: form.photo_url || null,
    });
    onOpenChange(false);
    toast.success('Saved to this sale / تم الحفظ');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="tailor-measurements-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-outfit">
            <Ruler size={18} className="text-primary" />Tailor Order / طلب خياطة
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs flex items-center gap-1"><User size={11} />Customer Name / اسم العميل</Label>
            <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="h-10 rounded-xl" data-testid="tailor-customer-name-input" />
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1"><Phone size={11} />Phone Number / رقم الجوال</Label>
            <Input type="tel" inputMode="tel" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="h-10 rounded-xl" data-testid="tailor-customer-phone-input" />
          </div>
        </div>

        <div>
          <Label className="text-xs flex items-center gap-1"><CalendarClock size={11} />Ready Date / تاريخ التسليم</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input type="date" value={form.ready_date} onChange={(e) => setForm({ ...form, ready_date: e.target.value })} className="h-10 rounded-xl flex-1" data-testid="tailor-ready-date-input" />
          </div>
          <div className="flex gap-2 mt-2">
            {QUICK_DAYS.map(({ days, label }) => (
              <Button
                key={days} type="button" size="sm" variant="outline"
                className={`flex-1 h-8 rounded-lg text-[11px] ${form.ready_date === plusDays(days) ? 'border-primary bg-primary/10 text-primary' : ''}`}
                onClick={() => setForm({ ...form, ready_date: plusDays(days) })}
                data-testid={`tailor-ready-quick-${days}d`}
              >{label}</Button>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 p-2.5 rounded-xl border border-border bg-muted/30 cursor-pointer select-none" data-testid="tailor-modification-toggle">
          <input
            type="checkbox"
            className="h-4 w-4 accent-primary"
            checked={!!form.modification_only}
            onChange={(e) => setForm({ ...form, modification_only: e.target.checked })}
            data-testid="tailor-modification-checkbox"
          />
          <span className="text-sm font-medium">Modification Only / تعديل فقط</span>
        </label>

        {form.modification_only ? (
          <div>
            <Label className="text-xs">Modification Notes / ملاحظات التعديل</Label>
            <Textarea
              rows={3}
              value={form.modification_notes}
              onChange={(e) => setForm({ ...form, modification_notes: e.target.value })}
              placeholder="e.g. shorten sleeves 2cm / تقصير الأكمام ٢ سم"
              className="rounded-xl mt-1"
              data-testid="tailor-modification-notes-input"
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map(({ key, label }) => (
              <div key={key}>
                <Label className="text-xs">{label}</Label>
                <Input
                  type="number" step="0.5" inputMode="decimal" placeholder="cm"
                  value={form.measurements?.[key] ?? ''}
                  onChange={(e) => setMeas(key, e.target.value)}
                  className="h-10 rounded-xl"
                  data-testid={`measurement-${key}-input`}
                />
              </div>
            ))}
          </div>
        )}

        <div className="mt-1">
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhoto} data-testid="measurement-photo-input" />
          {form.photo_url ? (
            <div className="flex items-center gap-2 p-2 rounded-xl border border-border bg-muted/40" data-testid="measurement-photo-preview">
              <img src={form.photo_url} alt="Measurement sheet" className="h-14 w-14 rounded-lg object-cover border" />
              <span className="text-xs text-muted-foreground flex-1 truncate">Reference photo / صورة مرجعية</span>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-error" onClick={() => setForm({ ...form, photo_url: '' })} data-testid="measurement-photo-remove">
                <X size={14} />
              </Button>
            </div>
          ) : (
            <Button variant="outline" className="w-full rounded-xl h-11" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="measurement-photo-btn">
              <Camera size={16} className="mr-2" />{uploading ? 'Uploading…' : 'Snap/Upload Photo / التقط أو ارفع صورة'}
            </Button>
          )}
        </div>

        <div className="flex gap-2 mt-2">
          <Button variant="outline" className="flex-1 rounded-xl" onClick={() => onOpenChange(false)} data-testid="measurements-cancel-btn">Cancel / إلغاء</Button>
          <Button className="flex-1 rounded-xl" onClick={handleSave} data-testid="measurements-save-btn">Save / حفظ</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TailorMeasurementsDialog;
